import { useState } from "react";
import { Button, Card, Form, Input, Select, Space, Typography, message } from "antd";
import { CustomerServiceOutlined, SendOutlined } from "@ant-design/icons";
import { useAuth } from "contexts/AuthContext";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { OLIVEEB_COLORS } from "workspaces/colors";
import { PROFILE_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const { Paragraph, Text, Title } = Typography;
const { TextArea } = Input;

const CATEGORY_OPTIONS = [
  "General Inquiry",
  "Technical Support",
  "Billing Issue",
  "Feedback",
  "Account Help",
].map((value) => ({ value, label: value }));

const INITIAL_VALUES = {
  category: "General Inquiry",
  message: "",
};

const Support = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const messageValue = Form.useWatch("message", form) || "";

  const submitHelpRequest = async (values) => {
    const content = String(values.message || "").trim();
    if (!content) {
      message.warning("Please type your response before clicking submit.");
      return;
    }

    try {
      setSubmitting(true);
      await apiClient.post(`${PROFILE_SERVICE_BASE_URL}/api/v1/help`, {
        category: values.category || "General Inquiry",
        message: content,
        sourceApp: "olitask-web-ui",
      });
      form.resetFields();
      message.success(`Message sent. Category: ${values.category || "General Inquiry"}`);
    } catch (error) {
      message.error(error?.message || "Failed to send help request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon={<CustomerServiceOutlined />}
          title="Help & Support"
          subtitle="Contact Olitask support"
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
      </div>

      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Card
          style={{
            borderRadius: 8,
            borderColor: OLIVEEB_COLORS.border,
            boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
          }}
          styles={{ body: { padding: 20 } }}
        >
          <div
            style={{
              position: "relative",
              overflow: "hidden",
              borderRadius: 8,
              padding: 18,
              marginBottom: 16,
              border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
              background: OLIVEEB_COLORS.card,
            }}
          >
            <div
              style={{
                position: "absolute",
                top: -28,
                right: -22,
                width: 120,
                height: 120,
                borderRadius: 60,
                background: OLIVEEB_COLORS.oliveebGreenSoft,
              }}
            />
            <Text
              style={{
                display: "block",
                color: OLIVEEB_COLORS.oliveebGreen,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: 1.2,
                textTransform: "uppercase",
              }}
            >
              Support Desk
            </Text>
            <Title level={3} style={{ color: OLIVEEB_COLORS.textPrimary, margin: "6px 0 4px" }}>
              How can we help?
            </Title>
            <Paragraph style={{ color: OLIVEEB_COLORS.oliveebDarkRed, marginBottom: 0 }}>
              Send us a quick note and we&apos;ll point you in the right direction.
            </Paragraph>
          </div>

          <Title level={4} style={{ color: OLIVEEB_COLORS.oliveebGreen, marginTop: 0 }}>
            Contact Us
          </Title>

          <Form
            form={form}
            layout="vertical"
            initialValues={INITIAL_VALUES}
            onFinish={submitHelpRequest}
            requiredMark={false}
          >
            <Form.Item label="Inquiry Category" name="category">
              <Select options={CATEGORY_OPTIONS} />
            </Form.Item>

            <Form.Item
              label="Message"
              name="message"
              rules={[{ required: true, message: "Please type your response before clicking submit." }]}
            >
              <TextArea
                rows={6}
                maxLength={500}
                showCount
                placeholder="Tell us what happened or what you need help with..."
              />
            </Form.Item>

            <Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
              <Text type="secondary">{messageValue.length}/500</Text>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SendOutlined />}
                loading={submitting}
                disabled={!user?.id && submitting}
                style={{
                  borderColor: OLIVEEB_COLORS.oliveebGreen,
                  background: OLIVEEB_COLORS.oliveebGreen,
                  fontWeight: 800,
                  borderRadius: 8,
                }}
              >
                Submit
              </Button>
            </Space>
          </Form>
        </Card>
      </Space>
    </div>
  );
};

export default Support;
